import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextButton from 'LuxKit/Button/TextButton'
import { CurrencyProvider } from 'contexts/currencyContext'
import { last } from 'lib/array/arrayUtils'

interface Props {
  journey: App.JourneyV2
  onSelect: (data: { journey: App.JourneyV2 }) => void
  isAlreadyAdded?: boolean
}

const CardContainer = styled(Sheet)`
  padding: ${rem(16)} ${rem(12)};
`

const AirlineLogo = styled.img`
  height: ${rem(32)};
  width: ${rem(32)};
  object-fit: contain;
  flex-shrink: 0;
`

function getStopsLabel(numberOfStops: number, flights: Array<App.FlightV2>, intl: IntlShape): string {
  if (numberOfStops === 0) {
    return intl.formatMessage({
      id: 'Cvc6NejS9VBmrNzgU3V9',
      defaultMessage: 'Direct',
      description: 'Label indicating a non-stop direct flight in the trip planner drawer flight card',
    })
  }
  if (numberOfStops === 1) {
    const layoverAirport = flights.length > 1 ? flights[0].arrivalAirport : ''
    return intl.formatMessage(
      {
        id: 'jW7AvVkBqCku8uiTVaDq',
        defaultMessage: '1 stop {airport}',
        description: 'Label for a single layover stop in the trip planner drawer flight card',
      },
      { airport: layoverAirport },
    )
  }
  return intl.formatMessage(
    {
      id: 'TMkiPFK3ZUXBJuoTfJvb',
      defaultMessage: '{count} stops',
      description: 'Label showing the number of layover stops in the trip planner drawer flight card',
    },
    { count: numberOfStops },
  )
}

function getLayoverDetail(numberOfStops: number, flights: Array<App.FlightV2>): string {
  if (numberOfStops === 0 || flights.length < 2) return ''
  const layoverAirport = flights[0].arrivalAirport
  const { hours, minutes } = flights[0].stopoverTime
  if (hours || minutes) {
    return `${hours}h ${minutes}m ${layoverAirport}`
  }
  return layoverAirport
}

function DrawerFlightCard({ journey, onSelect, isAlreadyAdded = false }: Props) {
  const intl = useIntl()

  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = last(flights)
  const duration = journey.flightGroup.totalFlightDuration
  const numberOfStops = journey.flightGroup.numberOfStops

  const logoSrc = journey.carrierBrandMark ?? journey.carrierLogo
  const routeLabel = `${firstFlight.departureAirport}-${lastFlight.arrivalAirport}`

  const durationLabel = `${duration.hours}h ${duration.minutes}m`

  const stopsLabel = useMemo(
    () => getStopsLabel(numberOfStops, flights, intl),
    [numberOfStops, flights, intl],
  )

  const layoverDetail = useMemo(
    () => getLayoverDetail(numberOfStops, flights),
    [numberOfStops, flights],
  )

  const selectLabel = intl.formatMessage({
    id: '_0VIOzDsllCBamkVS2jJ',
    defaultMessage: 'Select',
    description: 'Button label to select a flight in the trip planner drawer search results',
  })

  const addedLabel = intl.formatMessage({
    id: 'fcMoHMfQFYaWmopfEjet',
    defaultMessage: 'Added to trip',
    description: 'Disabled button label shown when a flight is already added to the trip planner itinerary',
  })
  const flightTimeRange = intl.formatMessage(
    {
      id: 'TytBkE-kwMsmq37l59e0',
      defaultMessage: '{departureTime} - {arrivalTime}',
      description: 'Flight departure and arrival time range shown in the trip planner flight drawer card; translators may reorder the time values and separator',
    },
    {
      departureTime: firstFlight.departingTime,
      arrivalTime: lastFlight.arrivalTime,
    },
  )

  const handleClick = useCallback(() => {
    onSelect({ journey })
  }, [onSelect, journey])

  return (
    <CardContainer border="neutral-five" borderRadius="L">
      <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" fullWidth>
        <Group direction="horizontal" gap={24} verticalAlign="center">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {logoSrc && <AirlineLogo src={logoSrc} alt={journey.carrierName} />}
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6" colour="neutral-two">
                {flightTimeRange}
              </Heading>
              <Caption variant="medium" colour="neutral-three">
                {journey.carrierName}
              </Caption>
            </Group>
          </Group>

          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" colour="neutral-two">{durationLabel}</BodyText>
            <Caption variant="medium" colour="neutral-three">{routeLabel}</Caption>
          </Group>

          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" colour="neutral-two">{stopsLabel}</BodyText>
            {layoverDetail && (
              <Caption variant="medium" colour="neutral-three">{layoverDetail}</Caption>
            )}
          </Group>
        </Group>

        <Group direction="horizontal" gap={16} verticalAlign="center">
          <CurrencyProvider currency={journey.currencyCode}>
            <PriceRowPrice size="M" price={journey.price.adult.totalFare ?? 0} saleUnit="adult" />
          </CurrencyProvider>

          <TextButton kind={isAlreadyAdded ? 'tertiary' : 'secondary'} size="small" onClick={handleClick} disabled={isAlreadyAdded}>
            {isAlreadyAdded ? addedLabel : selectLabel}
          </TextButton>
        </Group>
      </Group>
    </CardContainer>
  )
}

export default memo(DrawerFlightCard)
